import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import {
  ChevronDownIcon,
  ChevronUpIcon,
  Cross2Icon,
  PlusIcon,
} from '@radix-ui/react-icons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { SofaFormTypeGridSelect } from '@/components/shared/inputComponents/SofaFormTypeGridSelect'
import SofaBlueprint from '../../../configurator/SofaBlueprint'
import { loadSofaElement } from '@/configurator/DynamicLoadSofaShape'
import {
  getShapeChangeableProperties,
  type ChangeableProperties,
} from '@/configurator/utils/shapeConfigUtils'
import { dimensionFieldsConfig, DimensionFieldItem } from './dimensionFields'

const COMPOSITION_PATH = 'dimensions.composition'

// Flat composition contract (matches ERP CompositionItem and every COMPOSITE
// renderer copy): the "Length" field is the entry's footprint depth `height`;
// everything else writes flat onto the entry itself.
const memberFieldName = (index: number, key: string): string => {
  if (key === 'length') return `${COMPOSITION_PATH}.${index}.height`
  return `${COMPOSITION_PATH}.${index}.${key}`
}

// Not part of the flat composition contract and not read by any COMPOSITE
// renderer — showing them per-member would be dead controls.
const MEMBER_EXCLUDED_KEYS = new Set([
  'height',
  'seat_height',
  'seat_depth',
  'seat_width',
  'armrest_width',
  'mattress_width',
  'mattress_length',
])

function MemberPanel({ index }: { index: number }) {
  const { control, setValue } = useFormContext()
  const shape: string = useWatch({ name: `${COMPOSITION_PATH}.${index}.shape` })
  const entry = useWatch({ name: `${COMPOSITION_PATH}.${index}` })
  const [memberProps, setMemberProps] = useState<ChangeableProperties | null>(
    null
  )

  useEffect(() => {
    let cancelled = false
    if (!shape) return
    getShapeChangeableProperties(shape).then(props => {
      if (!cancelled) setMemberProps(props)
    })
    return () => {
      cancelled = true
    }
  }, [shape])

  return (
    <div className="border-t px-3 py-3 space-y-4">
      <SofaFormTypeGridSelect
        field={{
          value: shape ?? '',
          onChange: value =>
            setValue(`${COMPOSITION_PATH}.${index}.shape`, value, {
              shouldDirty: true,
            }),
        }}
      />
      <SofaBlueprint
        id={index}
        type={shape}
        dimensions={{ ...entry, length: entry?.height } as any}
        showMetrics
      />
      <div className="grid grid-cols-2 gap-4">
        {dimensionFieldsConfig.map(({ key, label, measurement }) => {
          if (MEMBER_EXCLUDED_KEYS.has(key)) return null
          // width/length are the member's stored footprint — always editable;
          // the rest is gated by the shape's changeable properties.
          if (key !== 'width' && key !== 'length' && !memberProps?.[key]) {
            return null
          }
          return (
            <InputFieldContainer
              key={key}
              label={label}
              variant="two-column"
              control={control}
              name={memberFieldName(index, key) as any}
              render={({ field }) => (
                <DimensionFieldItem
                  fieldKey={key}
                  measurement={measurement}
                  field={field}
                />
              )}
            />
          )
        })}
      </div>
    </div>
  )
}

function MemberRow({
  index,
  isFirst,
  isLast,
  expanded,
  onToggle,
  onMoveUp,
  onMoveDown,
  onRemove,
}: {
  index: number
  isFirst: boolean
  isLast: boolean
  expanded: boolean
  onToggle: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}) {
  const shape: string = useWatch({ name: `${COMPOSITION_PATH}.${index}.shape` })

  return (
    <div className="rounded-md border">
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left"
          onClick={onToggle}
        >
          <Badge variant="secondary" className="text-xs">
            {shape}
          </Badge>
        </button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={isFirst}
          onClick={onMoveUp}
        >
          <ChevronUpIcon />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={isLast}
          onClick={onMoveDown}
        >
          <ChevronDownIcon />
        </Button>
        <Button type="button" variant="ghost" size="icon" onClick={onRemove}>
          <Cross2Icon />
        </Button>
      </div>
      {expanded && <MemberPanel index={index} />}
    </div>
  )
}

export default function CompositeMembersEditor() {
  const { control } = useFormContext()
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: COMPOSITION_PATH as any,
  })
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)
  const [addOpen, setAddOpen] = useState(false)

  const handleAdd = async (typeName: string) => {
    const newIndex = fields.length
    const defaults = (await loadSofaElement(typeName))?.getDefaultSettings?.()
      ?.dimensions
    append({
      shape: typeName,
      width: defaults?.width ?? 100,
      height: defaults?.length ?? 100,
    })
    setExpandedIndex(newIndex)
  }

  return (
    <div className="space-y-2">
      {fields.length === 0 && (
        <p className="text-sm text-muted-foreground">No modules defined</p>
      )}
      <div className="flex flex-col gap-1">
        {fields.map((field, index) => (
          <MemberRow
            key={field.id}
            index={index}
            isFirst={index === 0}
            isLast={index === fields.length - 1}
            expanded={expandedIndex === index}
            onToggle={() =>
              setExpandedIndex(expandedIndex === index ? null : index)
            }
            onMoveUp={() => {
              move(index, index - 1)
              setExpandedIndex(null)
            }}
            onMoveDown={() => {
              move(index, index + 1)
              setExpandedIndex(null)
            }}
            onRemove={() => {
              remove(index)
              setExpandedIndex(null)
            }}
          />
        ))}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setAddOpen(true)}
      >
        <PlusIcon /> Add module
      </Button>
      <SofaFormTypeGridSelect
        onSelect={handleAdd}
        open={addOpen}
        onOpenChange={setAddOpen}
      />
    </div>
  )
}
